import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { EntityType } from "constants/index";
import { Routes } from "constants/routes";
import { useClientParamsHooks, useNavigate, useNavigationHooks, useUrlHooks } from "hooks";
import {
  useClientActions,
  useClientWorkspaceFoldersSelector,
  useDispatch,
  useUserActions,
  useWorkspaceColumnsSelector
} from "store";
import { ClientAction } from "store/actions";
import { IFolder, IMetaDetails, WorkspaceFilters } from "store/types";

export interface ClientWorkspaceContextValue {
  // State
  folders: IFolder[];
  /** Rows of the current view: folder rows first, then files — same shape the deal workspace table takes. */
  details: IMetaDetails[];
  detailsLoading: boolean;
  openedFolderIds: Set<number>;

  setOpenedFolderIds: React.Dispatch<React.SetStateAction<Set<number>>>;

  // Computed
  currentFolder: IFolder | null;
  folderTitle: string;
  isFolderOpen: (folderId: number) => boolean;

  // Actions
  onFolderArrowClick: (folderId: number) => void;
  openFolder: (folderId: number) => void;
  closeFolder: (folderId: number) => void;
  onFolderClick: (folderId: number) => void;
  getChildFolders: (parentId: number, folders?: IFolder[]) => IFolder[];
  getRootFolders: (folders: IFolder[]) => IFolder[];
  onWorkspaceFolderClick: () => void;
  onWorkspaceArrowClick: () => void;
  isWorkspaceFolderOpen: boolean;

  handleFetchNextPage: () => void;
  sortBy: { sort_by: string; sort: "asc" | "desc" } | null;
  setSortBy: (sortBy: { sort_by: string; sort: "asc" | "desc" } | null) => void;
  refetchFolderMetadata: () => Promise<void>;
  /** Optimistically patch a row, matched on its `entity_type` (`file`, `section`, …) and id. */
  patchDetail: (id: number, entityType: string, patch: Partial<IMetaDetails>) => void;
  /** Optimistically remove file rows from the table (e.g. after delete). */
  removeFiles: (fileIds: number[]) => void;
  /** Optimistically patch a single folder in the store (e.g. after rename). */
  patchFolder: (folderId: number, patch: Partial<IFolder>) => void;

  // Selection
  selectedItems: IMetaDetails[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IMetaDetails[]>>;

  // Column visibility
  columnVisibility: Record<string, boolean>;
  toggleColumn: (columnId: string) => void;

  // Column order
  columnOrder: string[];
  reorderColumns: (order: string[]) => void;

  // Filters
  activeFilters: WorkspaceFilters;
  handleChangeFilter: (filters: WorkspaceFilters) => void;

  /** True when viewing All Files or Recently Added (aggregate views). */
  isAggregateView: boolean;
}

export const ClientWorkspaceContext = createContext<ClientWorkspaceContextValue | null>(null);

const getAncestorIds = (folders: IFolder[], folderId: number): number[] => {
  const folder = folders.find((f) => f.id === folderId);
  if (!folder || folder.parent_id == null) return [];
  return [...getAncestorIds(folders, folder.parent_id), folder.parent_id];
};

interface ClientWorkspaceProviderProps {
  children: React.ReactNode;
}

export const ClientWorkspaceProvider: React.FC<ClientWorkspaceProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { clientId, folderId: folderIdFromUrl } = useClientParamsHooks();

  const { getClientFoldersAction, getClientFolderMetadataAction, patchClientWorkspaceFolderAction } =
    useClientActions();

  // ---- Route matching ----
  const isWorkspaceMatch = !!useRouteMatch({ path: Routes.clientWorkspace, exact: true });
  const isAllFilesMatch = !!useRouteMatch(Routes.clientWorkspaceAllFiles);
  const isFolderMatch = !!useRouteMatch(Routes.clientWorkspaceFolder);
  const isRecentlyAddedFilesMatch = !!useRouteMatch(Routes.clientWorkspaceRecentlyAdded);

  // Section for the table content:
  //   null → workspace root, -1 → all files, -1 + recently_added → recently added, number → folder contents.
  const { sectionId, recentlyAdded } = useMemo<{ sectionId: number | null; recentlyAdded: boolean }>(() => {
    if (isRecentlyAddedFilesMatch) return { sectionId: -1, recentlyAdded: true };
    if (isAllFilesMatch) return { sectionId: -1, recentlyAdded: false };
    if (isFolderMatch && folderIdFromUrl != null) return { sectionId: folderIdFromUrl, recentlyAdded: false };
    return { sectionId: null, recentlyAdded: false };
  }, [isRecentlyAddedFilesMatch, isAllFilesMatch, isFolderMatch, folderIdFromUrl]);

  const workspaceColumns = useWorkspaceColumnsSelector();
  const { updateWorkspaceColumnsAction } = useUserActions();
  const dispatch = useDispatch();

  // ---- Folders ----
  // Kept in the global store (clientModule.workspaceFolders) so the tree survives
  // tab switches without re-fetching; cleared on client change via ClearClient.
  const workspaceFoldersRecord = useClientWorkspaceFoldersSelector();
  const flatFolders = useMemo(() => Object.values(workspaceFoldersRecord), [workspaceFoldersRecord]);

  const fetchFolders = useCallback(async () => {
    if (!clientId || Number.isNaN(clientId)) return;
    try {
      // Client endpoint (GET /api/v0.0.3/clients/:clientId/folders). Exclude only
      // dataroom folders (client folders may not carry a `kind` at all).
      const folders = await getClientFoldersAction({ clientId, disableDispatch: true });
      const byId = folders
        .filter((folder) => folder.kind !== "dataroom")
        .reduce<Record<number, IFolder>>((acc, folder) => {
          acc[folder.id] = folder;
          return acc;
        }, {});
      dispatch({ type: ClientAction.SetWorkspaceFolders, payload: byId });
    } catch {
      // ignore
    }
  }, [clientId, getClientFoldersAction, dispatch]);

  // Fetch once when the store has no folders yet; a tab switch re-mounts the
  // provider but the cached folders remain, so no refetch happens.
  useEffect(() => {
    if (!flatFolders.length) {
      fetchFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRootFolders = useCallback((folders: IFolder[]) => folders.filter((f) => f.parent_id === null), []);

  const getChildFolders = useCallback(
    (parentId: number, folders: IFolder[] = flatFolders) => folders.filter((f) => f.parent_id === parentId),
    [flatFolders]
  );

  const currentFolder = useMemo(
    () => (folderIdFromUrl != null ? (flatFolders.find((f) => f.id === folderIdFromUrl) ?? null) : null),
    [flatFolders, folderIdFromUrl]
  );

  // ---- Table rows (folders + files) ----
  const [details, setDetails] = useState<IMetaDetails[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState<WorkspaceFilters>({});

  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const fetchingNextPageRef = useRef(false);
  const totalPagesRef = useRef(1);
  // Monotonic token: only the latest fetch is allowed to apply its result, so a
  // slow response can't clobber the state of a newer one (out-of-order races).
  const fetchGenerationRef = useRef(0);

  // ---- Sorting ----
  const [sortBy, setSortBy] = useState<{ sort_by: string; sort: "asc" | "desc" } | null>(null);

  const filterPayloadBuilder = useCallback((filters: WorkspaceFilters) => {
    const dateItem = filters?.last_updated?.[0];
    const dateRange = dateItem && typeof dateItem === "object" && "from" in dateItem ? dateItem : null;

    return {
      investor_ids: filters?.investor?.map((investor) => (investor as { id: number }).id) || undefined,
      file_type: filters?.file_type,
      tags: filters?.tag?.map((tag) => (tag as { id: number }).id) || undefined,
      classification: filters?.classification,
      file_status: filters?.file_status,
      document_type: filters?.document_type,
      currency: filters?.currency,
      source: filters?.source,
      ...(filters?.last_updated ? { last_updated: [{ from: dateRange?.from ?? "", to: dateRange?.to ?? "" }] } : {})
    };
  }, []);

  const fetchFiles = useCallback(
    async (append = false): Promise<boolean> => {
      if (!clientId || Number.isNaN(clientId)) {
        setDetailsLoading(false);
        return false;
      }
      const generation = ++fetchGenerationRef.current;
      setDetailsLoading(true);
      try {
        // Client endpoint (GET /clients/:clientId/workspace/folders). `sectionId`
        // selects the view: null → root, -1 → all files (+recently_added), number → folder.
        const metadata = await getClientFolderMetadataAction({
          clientId,
          sectionId,
          recently_added: recentlyAdded,
          filters: filterPayloadBuilder(activeFilters),
          page: pageRef.current,
          ...(sortBy ?? {})
        });
        if (generation !== fetchGenerationRef.current) return false;
        const rows = metadata.details ?? [];
        setDetails((prev) => (append ? [...prev, ...rows] : rows));
        totalPagesRef.current = metadata.last_page ?? 1;
        hasMoreRef.current = pageRef.current < totalPagesRef.current;
        return true;
      } catch {
        return false;
      } finally {
        if (generation === fetchGenerationRef.current) setDetailsLoading(false);
      }
    },
    [clientId, sectionId, recentlyAdded, activeFilters, sortBy, filterPayloadBuilder, getClientFolderMetadataAction]
  );

  const refetchFolderMetadata = useCallback(async () => {
    pageRef.current = 1;
    hasMoreRef.current = false;
    fetchingNextPageRef.current = false;
    // Refresh both the tree (so newly created/deleted folders appear) and the table.
    await fetchFolders();
    await fetchFiles(false);
  }, [fetchFolders, fetchFiles]);

  // Rows are keyed by (entity_type, id): folders arrive as `section` rows and their ids
  // come from a different table than the files', so both parts are needed to hit one row.
  const patchDetail = useCallback((id: number, entityType: string, patch: Partial<IMetaDetails>) => {
    setDetails((prev) =>
      prev.map((row) =>
        row.entity_type === entityType && row.id === id ? ({ ...row, ...patch } as IMetaDetails) : row
      )
    );
  }, []);

  const removeFiles = useCallback((fileIds: number[]) => {
    const ids = new Set(fileIds);
    setDetails((prev) => prev.filter((row) => !(row.entity_type === EntityType.FILE && ids.has(row.id))));
  }, []);

  const patchFolder = useCallback(
    (folderId: number, patch: Partial<IFolder>) => {
      patchClientWorkspaceFolderAction({ id: folderId, patch });
      setDetails((prev) =>
        prev.map((row) =>
          row.type === EntityType.FOLDER && row.id === folderId ? ({ ...row, ...patch } as IMetaDetails) : row
        )
      );
    },
    [patchClientWorkspaceFolderAction]
  );

  const handleFetchNextPage = useCallback(async () => {
    if (fetchingNextPageRef.current || !hasMoreRef.current) return;
    fetchingNextPageRef.current = true;
    const requestedPage = pageRef.current + 1;
    pageRef.current = requestedPage;
    const ok = await fetchFiles(true);
    if (!ok && pageRef.current === requestedPage) {
      pageRef.current = requestedPage - 1;
    }
    fetchingNextPageRef.current = false;
  }, [fetchFiles]);

  useEffect(() => {
    pageRef.current = 1;
    fetchFiles(false);
  }, [sectionId, recentlyAdded, activeFilters, sortBy]);

  // ---- Folder navigation state ----
  const [openedFolderIds, setOpenedFolderIds] = useState<Set<number>>(() => new Set());
  const [isWorkspaceFolderOpen, setIsWorkspaceFolderOpen] = useState(true);

  const isFolderOpen = useCallback((folderId: number) => openedFolderIds.has(folderId), [openedFolderIds]);

  const onFolderArrowClick = useCallback((folderId: number) => {
    setOpenedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const openFolder = useCallback((folderId: number) => {
    setOpenedFolderIds((prev) => (prev.has(folderId) ? prev : new Set(prev).add(folderId)));
  }, []);

  const closeFolder = useCallback((folderId: number) => {
    setOpenedFolderIds((prev) => {
      if (!prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
  }, []);

  const onFolderClick = useCallback(
    (folderId: number) => {
      navigate({
        route: "clientWorkspaceFolder",
        params: { client_id: clientId, folder_id: folderId },
        search: { section: folderId }
      });
    },
    [navigate, clientId]
  );

  const onWorkspaceFolderClick = useCallback(() => {
    navigate({ route: "clientWorkspace", params: { client_id: clientId }, setQueriesMethod: "drop" });
  }, [navigate, clientId]);

  const onWorkspaceArrowClick = useCallback(() => {
    setIsWorkspaceFolderOpen((prev) => !prev);
  }, []);

  // Auto-open ancestor folders on navigation
  useEffect(() => {
    if (folderIdFromUrl == null) return;
    const idsToOpen = getAncestorIds(flatFolders, folderIdFromUrl);
    if (idsToOpen.length > 0) {
      setOpenedFolderIds((prev) => new Set([...prev, ...idsToOpen]));
    }
    setIsWorkspaceFolderOpen(true);
  }, [folderIdFromUrl, flatFolders]);

  // ---- Selection ----
  const [selectedItems, setSelectedItems] = useState<IMetaDetails[]>([]);

  useEffect(() => {
    setSelectedItems([]);
  }, [folderIdFromUrl, sectionId, recentlyAdded]);

  // ---- Column visibility ----
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(workspaceColumns.map((column) => [column.id, column.visible]))
  );
  const toggleColumn = useCallback(
    (columnId: string) => {
      setColumnVisibility((prev) => {
        const next = { ...prev, [columnId]: !prev[columnId] };
        updateWorkspaceColumnsAction(Object.entries(next).map(([id, visible]) => ({ id, visible })));
        return next;
      });
    },
    [updateWorkspaceColumnsAction]
  );

  // ---- Column order ----
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const reorderColumns = useCallback((order: string[]) => {
    setColumnOrder(order);
  }, []);

  // ---- Filters ----
  const handleChangeFilter = useCallback((filters: WorkspaceFilters) => {
    setActiveFilters(filters);
  }, []);

  const folderTitle = useMemo(() => {
    if (isFolderMatch) return currentFolder?.title || "";
    if (isWorkspaceMatch) return "Workspace";
    if (isAllFilesMatch) return "All Items";
    if (isRecentlyAddedFilesMatch) return "Recently Added";
    return "";
  }, [currentFolder, isFolderMatch, isWorkspaceMatch, isAllFilesMatch, isRecentlyAddedFilesMatch]);

  // ---- Refetch from external trigger ----
  const { queryParams } = useUrlHooks();
  const { removeParams } = useNavigationHooks();

  useEffect(() => {
    if (queryParams.refetchWorkspace) {
      refetchFolderMetadata();
      fetchFolders();
      removeParams(["refetchWorkspace"]);
    }
  }, [queryParams.refetchWorkspace]);

  // ---- Context value ----
  const value = useMemo<ClientWorkspaceContextValue>(
    () => ({
      folders: flatFolders,
      details,
      detailsLoading,
      openedFolderIds,
      setOpenedFolderIds,
      currentFolder,
      folderTitle,
      isFolderOpen,
      onFolderArrowClick,
      openFolder,
      closeFolder,
      onFolderClick,
      getChildFolders,
      getRootFolders,
      onWorkspaceFolderClick,
      onWorkspaceArrowClick,
      isWorkspaceFolderOpen,
      handleFetchNextPage,
      sortBy,
      setSortBy,
      refetchFolderMetadata,
      patchDetail,
      removeFiles,
      patchFolder,
      selectedItems,
      setSelectedItems,
      columnVisibility,
      toggleColumn,
      columnOrder,
      reorderColumns,
      activeFilters,
      handleChangeFilter,
      isAggregateView: isAllFilesMatch || isRecentlyAddedFilesMatch
    }),
    [
      flatFolders,
      details,
      detailsLoading,
      openedFolderIds,
      currentFolder,
      folderTitle,
      isFolderOpen,
      onFolderArrowClick,
      openFolder,
      closeFolder,
      onFolderClick,
      getChildFolders,
      getRootFolders,
      onWorkspaceFolderClick,
      onWorkspaceArrowClick,
      isWorkspaceFolderOpen,
      handleFetchNextPage,
      sortBy,
      setSortBy,
      refetchFolderMetadata,
      patchDetail,
      removeFiles,
      patchFolder,
      selectedItems,
      setSelectedItems,
      columnVisibility,
      toggleColumn,
      columnOrder,
      reorderColumns,
      activeFilters,
      handleChangeFilter,
      isAllFilesMatch,
      isRecentlyAddedFilesMatch
    ]
  );

  return <ClientWorkspaceContext.Provider value={value}>{children}</ClientWorkspaceContext.Provider>;
};

export const useClientWorkspaceCtx = (): ClientWorkspaceContextValue => {
  const ctx = useContext(ClientWorkspaceContext);
  if (ctx == null) {
    throw new Error("useClientWorkspaceCtx must be used within ClientWorkspaceProvider");
  }
  return ctx;
};
